import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { FilterModel } from 'src/models';
import { AiCredits, AiPreviewJobDto, DnDesignerAddModel, MyAiDesign, MyAiDesignCard } from '../models';
import { BehaviorSubject, first, firstValueFrom, Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { BlobServiceClient } from '@azure/storage-blob';
import { AppUtils, Constants } from 'src/helpers';
import { DesignerAppImageUpsertModel } from '../models/dn-designer-image-upsert.model';
import { Guid } from 'guid-typescript';
import { BaseService, StorageService } from 'src/app/shared/services';
import { AssetQueryData, AssetsQueryResult } from '@cesdk/cesdk-js';
import { UserMediaAppService } from 'src/app/media-app/services';
import { UserMediaAppVideoUpsertModel } from 'src/app/media-app/models';
import { buildBulkDeleteByFilterPayload } from 'src/app/shared/services/bulk-delete-by-filter';

@Injectable({
  providedIn: 'root',
})
export class DnDesignerService {
  public apiEndPoint = environment.apiBaseUrl + 'api/dndesigner/';
  private CommonApiEndPoint = environment.apiBaseUrl + 'api/Common';

  // Shared AI-generation progress state. The fal.run polling loop in
  // generateImage/generateMenuBoard keeps running even after the component
  // that started it is destroyed (e.g. user navigates away), so this state
  // lives on the singleton service instead of the component — a freshly
  // mounted AiPromptBarComponent picks up an in-flight generation instead of
  // showing "not generating".
  isGenerating$ = new BehaviorSubject<boolean>(false);
  aiProgress$ = new BehaviorSubject<number>(0);
  aiCurrentQuote$ = new BehaviorSubject<string>('');
  aiQuoteVisible$ = new BehaviorSubject<boolean>(true);

  /** DN-4200/Task 5.3 fix round: onboarding-wizard's onBriefSubmitted() navigates into
   *  ImgLyComponent before its background previewDesign/attachGeneratedDesign cycle finishes —
   *  same cross-component-instance problem isGenerating$ above already solves, so the same
   *  pattern: state lives on this singleton service, not on either component, so it survives
   *  OnboardingWizardComponent being destroyed by the very navigation that starts the work.
   *  ImgLyComponent subscribes and filters by its own designId.
   *
   *  Final-review fix wave: ReplaySubject(1), not a plain Subject — ImgLyComponent's constructor
   *  subscribes only after the lazy-loaded Designer chunk + CE.SDK have finished initializing, so
   *  a fast background generation racing a slow chunk load could already have pushed its one event
   *  before that subscription exists. A plain Subject drops that event forever (isGeneratingSkeleton
   *  hangs with no recovery); ReplaySubject(1) buffers it for the late subscriber. */
  designGenerated$ = new ReplaySubject<{ designId: number; status: string; imglyBlobPath: string | null; containerName: string | null; errorMessage: string | null }>(1);

  private aiProgressInterval: ReturnType<typeof setInterval> | null = null;
  private aiQuoteInterval: ReturnType<typeof setInterval> | null = null;

  private readonly AI_QUOTES = [
    'Great screens tell great stories.',
    'Your content is about to look amazing.',
    'Pixels are being crafted just for you.',
    'Good things take a moment.',
    'Teaching pixels to behave…',
    'Making your screens unforgettable.',
    'Your audience will love this.',
    'Crafting something worth displaying.',
    'Every great screen starts here.',
    'Beauty takes a little time.',
    'Turning your vision into visuals.',
    'AI is working its magic.',
    'Almost ready to impress.',
    'The best designs are worth the wait.',
    'Pro tip: Ask Clara to leave spaces for pricing — you can add them later in Designer.',
  ];

  startAiProgress(): void {
    this.isGenerating$.next(true);
    this.aiProgress$.next(0);
    if (this.aiProgressInterval) { clearInterval(this.aiProgressInterval); }
    this.aiProgressInterval = setInterval(() => {
      const current = this.aiProgress$.value;
      if (current < 90) { this.aiProgress$.next(Math.min(90, current + 0.5)); }
    }, 1000);

    this.aiCurrentQuote$.next(this.AI_QUOTES[Math.floor(Math.random() * this.AI_QUOTES.length)]);
    this.aiQuoteVisible$.next(true);
    if (this.aiQuoteInterval) { clearInterval(this.aiQuoteInterval); }
    this.aiQuoteInterval = setInterval(() => {
      this.aiQuoteVisible$.next(false);
      setTimeout(() => {
        this.aiCurrentQuote$.next(this.AI_QUOTES[Math.floor(Math.random() * this.AI_QUOTES.length)]);
        this.aiQuoteVisible$.next(true);
      }, 400);
    }, 3500);
  }

  stopAiProgress(success: boolean): void {
    if (this.aiProgressInterval) { clearInterval(this.aiProgressInterval); this.aiProgressInterval = null; }
    if (this.aiQuoteInterval) { clearInterval(this.aiQuoteInterval); this.aiQuoteInterval = null; }
    this.aiProgress$.next(success ? 100 : 0);
    this.isGenerating$.next(false);
  }

  constructor(
    private http: HttpClient,
    public appUtils: AppUtils,
    public baseService: BaseService,
    private userMediaApp: UserMediaAppService,
    private storageService: StorageService,
  ) {}

  getPagedResult(params: any) {
    if (!params.groupId || params.groupId === -1) {
      params.groupId = null;
    }
    return this.http.get(
      this.apiEndPoint + 'paged-result', { params }
    );
  }

  getPagedList(params: any) {
    if (!params.groupId || params.groupId === -1) {
      params.groupId = null;
    }
    return this.http.get(
      this.apiEndPoint + 'paged-list', {params}
    );
  }

  getMediaAppCount(displayListId: number) {
    return this.http.get(this.apiEndPoint + `media-app-count/${displayListId}`);
  }

  add(model: DnDesignerAddModel): Observable<any> {
    return this.http.post(this.apiEndPoint + 'add', model);
  }

  generateDesign(dnDesignerId: number, brief: string): Observable<any> {
    return this.http.post(this.apiEndPoint + `${dnDesignerId}/generate`, { brief });
  }

  previewDesign(brief: string, format: string, attachmentUrl?: string): Observable<any> {
    return this.http.post(this.apiEndPoint + 'preview-design', { brief, format, attachmentUrl });
  }

  /** Same call as previewDesign(), just observing the full response so a caller can read the
   *  server's per-account quota headers (Task 0.3: X-Design-Quota-Used / X-Design-Quota-Limit,
   *  set by DNDesignerController.EnforceDesignQuotaAsync) off the same request instead of firing
   *  a second one just to inspect headers. */
  previewDesignWithQuota(brief: string, format: string, attachmentUrl?: string): Observable<HttpResponse<any>> {
    return this.http.post(this.apiEndPoint + 'preview-design', { brief, format, attachmentUrl }, { observe: 'response' });
  }

  /** Starts a design generation in the background and returns at once with a job to poll — a generation takes
   *  minutes, so it must never be one long-held request. 409 (carrying the running job) when one is already going. */
  startPreviewJob(brief: string, format: string, attachmentUrl?: string): Observable<AiPreviewJobDto> {
    return this.http.post<AiPreviewJobDto>(this.apiEndPoint + 'preview-design/start', { brief, format, attachmentUrl });
  }

  /** The account's AI credits for this month, and what each kind of design costs. */
  getAiCredits(): Observable<AiCredits> {
    return this.http.get<AiCredits>(this.apiEndPoint + 'ai-credits');
  }

  getPreviewJob(jobId: string): Observable<AiPreviewJobDto> {
    return this.http.get<AiPreviewJobDto>(this.apiEndPoint + `preview-design/status/${encodeURIComponent(jobId)}`);
  }

  attachGeneratedDesign(dnDesignerId: number, previewToken: string): Observable<any> {
    return this.http.post(this.apiEndPoint + `${dnDesignerId}/attach-generated`, { previewToken });
  }

  getMyAiDesigns(): Observable<MyAiDesign[]> {
    return this.http.get<MyAiDesign[]>(this.apiEndPoint + 'my-ai-designs');
  }

  // The preview lives in the user's own private container, so each card's thumbnail needs a
  // SAS-signed URL — one token for the whole list rather than one per card.
  async getMyAiDesignCards(): Promise<MyAiDesignCard[]> {
    const designs = await firstValueFrom(this.getMyAiDesigns());
    if (designs.length === 0) return [];
    const sasToken = (await firstValueFrom(this.storageService.getAzureSasToken())) as string;
    return designs.map((d) => ({
      ...d,
      thumbUrl: d.previewImagePath ? this.getBlobUrl(d.containerName, d.previewImagePath, sasToken) : null,
    }));
  }

  publishAsTemplate(dnDesignerId: number, label: string, groups: string[]): Observable<{ templateId: string }> {
    return this.http.post<{ templateId: string }>(this.apiEndPoint + `${dnDesignerId}/publish-as-template`, { label, groups });
  }

  reviseDesign(dnDesignerId: number, instruction: string): Observable<any> {
    return this.http.post(this.apiEndPoint + `${dnDesignerId}/revise`, { instruction });
  }

  async uploadFileForAi(file: File): Promise<string> {
    const sasToken = await firstValueFrom(this.storageService.getAzureSasToken());
    const containerName = this.appUtils.resolveMediaContainerName(null, []);
    // Every other upload flow (img-ly, onboarding-wizard media upload, bulk-upload)
    // provisions the container first — it's the only path that sets the container's
    // PublicAccess=Blob, which the AI generate/attach flow depends on since the
    // external DesignAgentService reads the attachment URL back anonymously.
    // Skipping this meant uploads here landed in a non-public container and 404'd.
    await firstValueFrom(this.storageService.createContainerIfNotExists(containerName!));
    const guid = Guid.create().toString();
    const ext = this.appUtils.getFileExtension(file.name);
    const blobPath = `${environment.azureContainers.designerUploads}/${guid}.${ext}`;
    const blobUrl = `${environment.azureContainerBaseUrl}/${containerName}/${blobPath}`;
    const blobServiceClient = new BlobServiceClient(`${environment.azureContainerBaseUrl}?${sasToken}`);
    const blockBlobClient = blobServiceClient.getContainerClient(containerName!).getBlockBlobClient(blobPath);
    await blockBlobClient.uploadData(file, { blockSize: 262144 });
    return blobUrl;
  }

  async generateImage(prompt: string, imageUrl?: string, aspectRatio: 'landscape' | 'portrait' = 'landscape'): Promise<string | null> {
    const imageSize = aspectRatio === 'portrait' ? 'portrait_16_9' : 'landscape_16_9';
    const endpoint = imageUrl
      ? 'https://queue.fal.run/openai/gpt-image-2/edit'
      : 'https://queue.fal.run/openai/gpt-image-2';
    const body: any = { prompt, image_size: imageSize };
    if (imageUrl) { body['image_urls'] = [imageUrl]; }

    const queue = await firstValueFrom(
      this.http.post<any>(this.apiEndPoint + 'proxy-fal', body, {
        headers: { 'x-fal-target-url': endpoint },
      })
    );

    const statusUrl: string = queue?.status_url;
    const responseUrl: string = queue?.response_url;
    if (!responseUrl) return null;

    const maxAttempts = 90;
    const pollIntervalMs = 2000;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
      const status = await firstValueFrom(
        this.http.get<any>(this.apiEndPoint + 'proxy-fal', {
          headers: { 'x-fal-target-url': statusUrl },
        })
      );
      if (status?.images?.[0]?.url) return status.images[0].url;
      if (status?.status === 'COMPLETED') break;
      if (status?.status === 'FAILED') return null;
    }

    const result = await firstValueFrom(
      this.http.get<any>(this.apiEndPoint + 'proxy-fal', {
        headers: { 'x-fal-target-url': responseUrl },
      })
    );
    return result?.images?.[0]?.url ?? null;
  }

  async generateMenuBoard(
    menuImageUrl: string,
    prompt: string,
    aspectRatio: 'landscape' | 'portrait'
  ): Promise<string | null> {
    const endpoint = 'https://queue.fal.run/openai/gpt-image-2/edit';
    const imageSize = aspectRatio === 'portrait' ? 'portrait_16_9' : 'landscape_16_9';
    const body = { image_urls: [menuImageUrl], prompt, image_size: imageSize };

    const queue = await firstValueFrom(
      this.http.post<any>(this.apiEndPoint + 'proxy-fal', body, {
        headers: { 'x-fal-target-url': endpoint },
      })
    );

    const statusUrl: string = queue?.status_url;
    const responseUrl: string = queue?.response_url;
    if (!responseUrl) return null;

    const maxAttempts = 90;
    const pollIntervalMs = 2000;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
      const status = await firstValueFrom(
        this.http.get<any>(this.apiEndPoint + 'proxy-fal', {
          headers: { 'x-fal-target-url': statusUrl },
        })
      );
      if (status?.images?.[0]?.url) return status.images[0].url;
      if (status?.status === 'COMPLETED') break;
      if (status?.status === 'FAILED') return null;
    }

    const result = await firstValueFrom(
      this.http.get<any>(this.apiEndPoint + 'proxy-fal', {
        headers: { 'x-fal-target-url': responseUrl },
      })
    );
    return result?.images?.[0]?.url ?? null;
  }

  exists(filterModel: FilterModel, folderId?: number): Observable<any> {
    if (!filterModel.groupId || filterModel.groupId === -1) {
      filterModel.groupId = null;
    }
    if (folderId == undefined) {
      folderId = null;
    }
    return this.http.get(
      `${this.apiEndPoint}exists/${filterModel.groupId}/${folderId}?mineOnly=${filterModel.mineOnly}`
    );
  }

  folderExists(groupId?: number, mineOnly?: boolean) {
    if (groupId == undefined || groupId == -1) {
      groupId = null;
    }
    if (mineOnly == undefined) {
      mineOnly = null;
    }

    return this.http.get(
      `${this.apiEndPoint}has-folder/${groupId}?mineOnly=${mineOnly}`
    );
  }

  edit(model: any): Observable<any> {
    return this.http.post(this.apiEndPoint + 'edit', model);
  }

  published(model: any): Observable<any> {
    return this.http.post(this.apiEndPoint + 'published', model);
  }

  getForEdit(id: number): Observable<any> {
    return this.http.get(this.apiEndPoint + 'get-for-edit/' + id);
  }
  GetDesignerTemplateJSON(url: string, token: string): Observable<any> {
    return this.http.get(`${url}`, {headers: {responseType: 'json'}});
  }

  delete(id: number) {
    return this.http.post(this.apiEndPoint + 'delete/' + id, null);
  }

  deleteBulk(ids: number[]) {
    return this.http.post(this.apiEndPoint + 'delete-bulk', ids);
  }

  deleteBulkByFilter(filterModel: any, excludedIds: number[] = []) {
    return this.http.post(this.apiEndPoint + 'delete-bulk-by-filter', buildBulkDeleteByFilterPayload(filterModel, excludedIds));
  }
  ProxyFalCall(model: any): Observable<any> {
    return this.http.post(this.apiEndPoint + 'proxy-fal', model);
  }
  getFolderList(filterKey: string, groupId?: number, mineOnly?: boolean) {
    if (groupId == undefined) {
      groupId = null;
    }
    if (mineOnly == undefined) {
      mineOnly = null;
    }
    return this.http.get(
      `${this.apiEndPoint}folders/${groupId}?mineOnly=${mineOnly}&filterKey=${filterKey}`
    );
  }
  addExport(model: any): Observable<any> {
    return this.http.post(this.apiEndPoint + 'add-dndesigner-export', model);
  }
  async uploadImage(
    imageItem: DesignerAppImageUpsertModel,
    sasToken: any,
    containerName: string,
    existingMediaApp: string
  ): Promise<any> {
    imageItem.fileUploadPercent = 1;
    return new Promise((resolve, reject) => {
      const CloudConfig = {
        sas: sasToken.toString(),
        storageAccount: environment.azureAccount,
        containerName: containerName,
      };
      const guid = existingMediaApp ? existingMediaApp : Guid.create();
      const fileName = `${guid}.${this.appUtils.getFileExtension(
        imageItem.file.name
      )}`;
      const tempFileName = `${environment.azureContainers.userMediaAppImage}/${fileName}`;
      const blobUrl = `${environment.azureContainerBaseUrl}/${CloudConfig.containerName}/${tempFileName}`;
      const blobServiceClientUrl = `${environment.azureContainerBaseUrl}?${sasToken}`;
      const blobServiceClient = new BlobServiceClient(blobServiceClientUrl);
      const containerClient = blobServiceClient.getContainerClient(
        CloudConfig.containerName
      );
      const blockBlobClient = containerClient.getBlockBlobClient(tempFileName);

      blockBlobClient
        .uploadData(imageItem.file, {
          blockSize: 262144,
          onProgress: (progress) => {
            imageItem.fileUploadPercent = Math.round(
              (progress.loadedBytes / imageItem.file.size) * 100
            );
          },
        })
        .then(() => {
          Constants.interceptAuthToken = true;
          imageItem.fileName = fileName;
          imageItem['originalFileName'] = fileName;
          imageItem.fileUrl = blobUrl;
          resolve(imageItem);
        })
        .catch((err) => {
          Constants.interceptAuthToken = true;
          this.baseService.error('Image file upload timeout.');
          reject(new Error(err));
        });
    });
  }
  async uploadVideo(videoItem: any, sasToken: any, containerName: string, existingMediaApp: any): Promise<any> {
          videoItem.fileUploadPercent = 1;
  
          const blockSize = this.appUtils.getBlockSize(videoItem.file.size);
          const maxRetries = 3;
          const baseDelay = 2000; // 2 seconds base delay
  
          return new Promise((resolve, reject) => {
              const CloudConfig = {
                  sas: sasToken.toString(),
                  storageAccount: environment.azureAccount,
                  containerName: containerName
              };
              
            const guid = existingMediaApp ? existingMediaApp : Guid.create();
            const fileName = `${guid}.${this.appUtils.getFileExtension(
            videoItem.file.name
            )}`;
              const tempFileName = `${environment.azureContainers.userMediaAppVideo}/${fileName}`;
              const blobUrl = `${environment.azureContainerBaseUrl}/${CloudConfig.containerName}/${tempFileName}`;
  
              const blobServiceClientUrl = `${environment.azureContainerBaseUrl}?${sasToken}`;
              const blobServiceClient = new BlobServiceClient(blobServiceClientUrl);
              const containerClient = blobServiceClient.getContainerClient(
                  CloudConfig.containerName
              );
              const blockBlobClient = containerClient.getBlockBlobClient(tempFileName);
  
              const options = {
                  blockSize: blockSize,
                  maxSingleShotSize: 1024 * 32,
                  onProgress: (progress) => {
                      videoItem.fileUploadPercent = Math.round(
                          (progress.loadedBytes / videoItem.file.size) * 100
                      );
                  }
              };
  
              // Retry logic with exponential backoff
              let attempt = 0;
              const attemptUpload = async (): Promise<void> => {
                  try {
                      attempt++;
                      console.log(`Upload attempt ${attempt} for file: ${videoItem.file.name}`);
                      
                      await blockBlobClient.uploadData(videoItem.file, options);
                      
                      // Success
                      Constants.interceptAuthToken = true;
                      videoItem.fileName = fileName;
                      videoItem.fileUrl = blobUrl;
                      console.log(`Upload successful on attempt ${attempt} for file: ${videoItem.file.name}`);
                      resolve(videoItem);
                      
                  } catch (err) {
                      console.error(`Upload attempt ${attempt} failed for file: ${videoItem.file.name}`, err);
                      
                      // Determine if we should retry based on error type
                      const shouldRetry = this.shouldRetryUpload(err, attempt, maxRetries);
                      
                      if (shouldRetry) {
                          const delay = baseDelay * Math.pow(2, attempt - 1); // Exponential backoff
                          console.log(`Retrying upload in ${delay}ms (attempt ${attempt}/${maxRetries})`);
                          
                          setTimeout(() => {
                              attemptUpload();
                          }, delay);
                      } else {
                          // Final failure
                          Constants.interceptAuthToken = true;
                          
                          const errorMessage = this.getErrorMessage(err, videoItem.file.name);
                          this.baseService.error(errorMessage);
                          reject(new Error(err));
                      }
                  }
              };
  
              // Start the upload process
              attemptUpload();
          });
      }
       private getErrorMessage(error: any, fileName: string): string {
        const info = this.appUtils.describeUploadError(error);

        // Always log the real Azure error (status / code / request id) so upload
        // failures are diagnosable instead of being hidden behind a generic message.
        console.error(
            `[VideoUpload] Blob upload failed for ${fileName} | ${info.summary}`,
            error
        );

        const errorMessage = String(info.message || '').toLowerCase();
        const errorCode = String(info.code || '').toLowerCase();
        const status = info.statusCode;
        const ref = info.summary ? ` (ref: ${info.summary})` : '';

        if (errorMessage.includes('timeout') || errorCode.includes('timeout')) {
            return `Upload timeout for ${fileName}. Please check your internet connection and try again.`;
        }

        if (
            errorMessage.includes('network') ||
            errorMessage.includes('connection') ||
            errorCode.includes('econnreset')
        ) {
            return `Network error while uploading ${fileName}. A corporate firewall or proxy can interrupt large video uploads — try a different network, then contact support if it persists${ref}.`;
        }

        if (status === 401 || errorMessage.includes('unauthorized') || errorCode.includes('401')) {
            return `Authentication error while uploading ${fileName}. Please refresh the page and try again.`;
        }

        if (
            status === 403 ||
            errorCode.includes('authenticationfailed') ||
            errorMessage.includes('forbidden') ||
            errorCode.includes('403')
        ) {
            return `Access denied while uploading ${fileName}. Your upload session may have expired — please refresh the page and try again; contact support if it persists${ref}.`;
        }

        if (status === 413 || errorCode.includes('requestbodytoolarge')) {
            return `${fileName} is too large to upload. Please contact support${ref}.`;
        }

        if (errorMessage.includes('quota') || errorMessage.includes('storage')) {
            return `Storage quota exceeded for ${fileName}. Please contact support.`;
        }

        // Default — now carries a support reference (Azure status/code/request id).
        return `Failed to upload ${fileName}. Please try again or contact support if the problem persists${ref}.`;
    }

       private shouldRetryUpload(error: any, attempt: number, maxRetries: number): boolean {
        if (attempt >= maxRetries) {
            return false;
        }

        // Retry on network-related errors
        const retryableErrors = [
            'NetworkError',
            'TimeoutError',
            'ECONNRESET',
            'ENOTFOUND',
            'ETIMEDOUT',
            'ECONNREFUSED',
            'timeout',
            'network',
            'connection'
        ];

        const errorMessage = error?.message?.toLowerCase() || '';
        const errorCode = error?.code?.toLowerCase() || '';
        const errorName = error?.name?.toLowerCase() || '';

        return retryableErrors.some(retryableError => 
            errorMessage.includes(retryableError.toLowerCase()) ||
            errorCode.includes(retryableError.toLowerCase()) ||
            errorName.includes(retryableError.toLowerCase())
        );
    }
  async uploadImageToDesignerUploads(
    imageItem: DesignerAppImageUpsertModel,
    sasToken: any,
    containerName: string,
    existingMediaApp: string
  ): Promise<any> {
    imageItem.fileUploadPercent = 1;
    return new Promise((resolve, reject) => {
      const CloudConfig = {
        sas: sasToken.toString(),
        storageAccount: environment.azureAccount,
        containerName: containerName,
      };
      const guid = existingMediaApp ? existingMediaApp : Guid.create();
      const fileName = `${guid}.${this.appUtils.getFileExtension(
        imageItem.file.name
      )}`;
      const tempFileName = `${environment.azureContainers.designerUploads}/${fileName}`;
      const blobUrl = `${environment.azureContainerBaseUrl}/${CloudConfig.containerName}/${tempFileName}`;
      const blobServiceClientUrl = `${environment.azureContainerBaseUrl}?${sasToken}`;
      const blobServiceClient = new BlobServiceClient(blobServiceClientUrl);
      const containerClient = blobServiceClient.getContainerClient(
        CloudConfig.containerName
      );
      const blockBlobClient = containerClient.getBlockBlobClient(tempFileName);

      blockBlobClient
        .uploadData(imageItem.file, {
          blockSize: 262144,
          onProgress: (progress) => {
            imageItem.fileUploadPercent = Math.round(
              (progress.loadedBytes / imageItem.file.size) * 100
            );
          },
        })
        .then(() => {
          Constants.interceptAuthToken = true;
          imageItem.fileName = fileName;
          imageItem['originalFileName'] = fileName;
          imageItem.fileUrl = blobUrl;
          resolve(imageItem);
        })
        .catch((err) => {
          Constants.interceptAuthToken = true;
          this.baseService.error('Image file upload timeout.');
          reject(new Error(err));
        });
    });
  }

  async uploadPdf(
    pdfItem: DesignerAppImageUpsertModel,
    sasToken: any,
    containerName: string,
    existingMediaApp: string
  ): Promise<any> {
    pdfItem.fileUploadPercent = 1;
    return new Promise((resolve, reject) => {
      const CloudConfig = {
        sas: sasToken.toString(),
        storageAccount: environment.azureAccount,
        containerName: containerName,
      };
      const guid = existingMediaApp ? existingMediaApp : Guid.create();
      const fileName = `${Guid.create()}.${this.appUtils.getFileExtension(
        pdfItem.file.name
      )}`;
      const tempFileName = `${environment.azureContainers.userMediaAppPdf}/${fileName}`;
      const blobUrl = `${environment.azureContainerBaseUrl}/${CloudConfig.containerName}/${tempFileName}`;
      const blobServiceClientUrl = `${environment.azureContainerBaseUrl}?${sasToken}`;
      const blobServiceClient = new BlobServiceClient(blobServiceClientUrl);
      const containerClient = blobServiceClient.getContainerClient(
        CloudConfig.containerName
      );
      const blockBlobClient = containerClient.getBlockBlobClient(tempFileName);

      blockBlobClient
        .uploadData(pdfItem.file, {
          blockSize: 262144,
          onProgress: (progress) => {
            pdfItem.fileUploadPercent = Math.round(
              (progress.loadedBytes / pdfItem.file.size) * 100
            );
          },
        })
        .then(() => {
          Constants.interceptAuthToken = true;
          pdfItem.fileName = fileName;
          pdfItem.fileUrl = blobUrl;
          resolve(pdfItem);
        })
        .catch((err) => {
          Constants.interceptAuthToken = true;
          this.baseService.error('Pdf file upload timeout.');
          reject(new Error(err));
        });
    });
  }
  getSceneUrl(
    containerName: string,
    assetFileName: string,
    sasToken: string
  ): string {
    const CloudConfig = {
      sas: sasToken.toString(),
      storageAccount: environment.azureAccount,
      containerName: containerName,
    };

    const tempFileName = `${environment.azureContainers.dnDesigner}/${assetFileName}/${environment.azureContainers.unZippedFolderName}/scene.scene`;
    const blobUrl = `${environment.azureContainerBaseUrl}/${containerName}/${tempFileName}`;
    const blobServiceClientUrl = `${environment.azureContainerBaseUrl}?${sasToken}`;
    const blobServiceClient = new BlobServiceClient(blobServiceClientUrl);
    const containerClient = blobServiceClient.getContainerClient(
      CloudConfig.containerName
    );
    const blockBlobClient = containerClient.getBlockBlobClient(tempFileName);
    return blockBlobClient.url;
  }
  getBlobUrl(containerName: string, blobPath: string, sasToken: string): string {
    const blobServiceClientUrl = `${environment.azureContainerBaseUrl}?${sasToken}`;
    const blobServiceClient = new BlobServiceClient(blobServiceClientUrl);
    const containerClient = blobServiceClient.getContainerClient(containerName);
    const blockBlobClient = containerClient.getBlockBlobClient(blobPath);
    return blockBlobClient.url;
  }
  getList(groupId?: number, model?: FilterModel): Observable<any> {
    if (!groupId || groupId === -1) {
      groupId = null;
    }

    if (model == null || model == undefined) {
      return this.http.get(this.apiEndPoint + 'list/' + groupId);
    }
    if (model.offset == undefined) {
      model.offset = null;
    }
    if (model.limit == undefined) {
      model.limit = null;
    }
    if (model.filterKey == undefined || model.filterKey == null) {
      model.filterKey = '';
    }

    return this.http.get(
      this.apiEndPoint + 'list/' + groupId + '?' + model.toQueryString()
    );
  }

  getSelectListItems(groupId: any, id: number = null) {
    if (groupId === '') {
      groupId = null;
    }
    return this.http.get(`${this.apiEndPoint}get-select-list-items/${groupId}`);
  }

  getChildItems(displayIds: string) {
    return this.http.get(
      this.apiEndPoint + 'child-items?dnDesignerIds=' + displayIds
    );
  }

  getSelectListItemsForGroupEdit(groupId: any) {
    if (!groupId || groupId === -1) {
      groupId = null;
    }
    return this.http.get(
      this.apiEndPoint + 'get-select-list-items-for-group-edit/' + groupId
    );
  }
  async searchUnsplash(
    query: string = '',
    page: number = 1,
    perPage: number = 5
  ): Promise<AssetsQueryResult> {
    const response: any = await firstValueFrom(this.SearchUnsplash({ query, page, perPage }));
    return response;
  }
  async searchPhotos(query: string, page: number = 1, perPage: number = 20) {
    const response: any = await firstValueFrom(this.SearchPexel({ query, page, perPage }));
    return response;
  }
  
  async searchPexelsVideos(query: string, page: number = 1, perPage: number = 20): Promise<AssetsQueryResult> {
    const response: any = await firstValueFrom(this.SearchPexelVideos({ query, page, perPage }));
    return response;
  }

  async GetExistingMedia(groupId, query: string) {
    const media: any = await firstValueFrom(
      this.userMediaApp.GetMediaList(groupId, query)
    );
    const assets = await this.mappedAssets(media);
    return assets;
  }
  async mappedAssets(assets: Array<any>) {
    assets = assets.filter(row => row.image);
    const mapped = await Promise.all(
      assets.map(async (el) => {
        // const img = await this.getImageDimensions(el.image.fileUrl);
        return {
          id: el.image.fileName,
          label: el.title,
          thumbUri: el.image.fileUrl,
          uri: el.image.fileUrl,
          meta: {
            thumbUri: el.image.fileUrl,
            // width: img.width,
            // height: img.height,
            mimeType: 'image/jpeg',
            uri: el.image.fileUrl,
          },
          context: {
            sourceId: 'custom-media',
          },
        };
      })
    );
    return mapped;
  }

  private async getImageDimensions(
    url: string
  ): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = () => resolve({ width: 0, height: 0 });
      img.src = url;
    });
  }
  SearchUnsplash(params: any) {
    return this.http.get(`${this.CommonApiEndPoint}/search-unsplash`, {
      params: {
        ...params,
        query: params.query || 'nature',
      },
    });
  }
  SearchPexel(params: any) {
    return this.http.get(`${this.CommonApiEndPoint}/get-pexels-photos`, {
      params: {
        ...params,
        query: params.query || 'nature',
      },
    });
  }
  
  SearchPexelVideos(params: any) {
    return this.http.get(`${this.CommonApiEndPoint}/get-pexels-videos`, {
      params: {
        ...params,
        query: params.query || 'nature',
      },
    });
  }
}
